import { useAppContext } from '@common/contexts/AppContext';
import { Rate, RatesFilterType } from '@features/rates/types';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useQueries, useQuery } from '@tanstack/react-query';
import excludeFalseyValues from '@utils/excludeFalseyValues';
import { toCamelCase } from 'case-converter';
import { getAvailableRoomById, roomsKeys, useStaticRooms } from '../rooms';
import {
  byPriorityThenPrice,
  byRoomAvailabilityThenPriorityThenPrice,
  toRatesByAvailability,
} from './mappers';
import {
  RateParams,
  RateParamsWithCodes,
  getAvailableRate,
  getAvailableRates,
  getRatePageSettings,
  getStaticRate,
  getStaticRates,
} from './requests';
import { RateSchema } from './schemas';

type AvailableRatesParams = Parameters<typeof getAvailableRates>[0];
type StaticRateParams = Parameters<typeof getStaticRates>;

export const ratesKeys = {
  rates: ['rates'] as const,
  staticRates: (...[params]: StaticRateParams) => {
    const sanitizedParams = excludeFalseyValues(params);

    if (Object.keys(sanitizedParams).length > 0) {
      return [...ratesKeys.rates, 'static', sanitizedParams] as const;
    }

    return [...ratesKeys.rates, 'static'] as const;
  },
  staticRate: (params: { rateCode: string; siteSlug: string; language?: string }) =>
    [...ratesKeys.rates, 'static', params] as const,
  availableRates: (params: AvailableRatesParams) =>
    [...ratesKeys.rates, 'available', excludeFalseyValues(params)] as const,
  availableRate: (...[rateCode, params]: Parameters<typeof getAvailableRate>) =>
    [...ratesKeys.rates, 'available', [rateCode, excludeFalseyValues(params)]] as const,
  roomRates: (...[params]: Parameters<typeof getAvailableRoomById>) =>
    [...ratesKeys.rates, 'room', [params]] as const,
  rateSettings: ({ siteId, language }: { siteId: number; language: string }) =>
    [...ratesKeys.rates, 'settings', { siteId, language }] as const,
};

export function useStaticRates(...[params]: StaticRateParams) {
  return useQuery({
    queryKey: ratesKeys.staticRates(params),
    queryFn: () => getStaticRates(params),
  });
}

export function useStaticRate(rateCode: string, language?: string) {
  const { siteSlug } = useAppContext();

  return useQuery({
    queryKey: ratesKeys.staticRate({ siteSlug, rateCode, language }),
    queryFn: () => getStaticRate({ siteSlug, rateCode, language }),
  });
}

export function useRates(
  params: RateParamsWithCodes & { type?: RatesFilterType; enabled?: boolean; language?: string }
) {
  const { siteId, siteSlug } = useAppContext();
  const isMultiRoomsEnabled =
    useFeatureIsOn('feature.multi-room.enabled') && params.rooms.length > 1;
  const { enabled: enabledParam, type, language, ...rateParams } = params;
  const enabled = enabledParam ?? true;

  const query = useQueries({
    queries: [
      {
        queryKey: ratesKeys.staticRates({
          siteSlug,
          promoRateCode: rateParams.promoRateCode,
          type,
          language,
        }),
        queryFn: () =>
          getStaticRates({ siteSlug, promoRateCode: rateParams.promoRateCode, type, language }),
        cacheTime: Infinity,
        staleTime: Infinity,
        keepPreviousData: true,
        enabled,
      },
      {
        queryKey: ratesKeys.availableRates({ siteId, isMultiRoomsEnabled, type, ...rateParams }),
        queryFn: () => getAvailableRates({ siteId, isMultiRoomsEnabled, type, ...rateParams }),
        keepPreviousData: true,
        enabled,
      },
    ],
  });

  const [staticRatesQuery, availableRatesQuery] = query;
  const staticRates = staticRatesQuery.data?.data ?? [];
  const availableRates = availableRatesQuery.data?.data ?? [];

  const rates = toRatesByAvailability(staticRates, availableRates);

  return {
    rates,
    isFetching: staticRatesQuery.isFetching || availableRatesQuery.isFetching,
    error: staticRatesQuery.error || availableRatesQuery.error,
    // @TODO: do we return query just for testing? could be avoided if use mock fns?
    query,
  };
}

export function useRate(rateCode: string, params: RateParamsWithCodes, language?: string) {
  const { siteSlug, siteId } = useAppContext();
  const isMultiRoomsEnabled =
    useFeatureIsOn('feature.multi-room.enabled') && params.rooms.length > 1;

  const staticRoomsQuery = useStaticRooms();
  const roomPriorities = new Map(
    (staticRoomsQuery.data?.data ?? []).map((room) => [room.room_id, room.priority ?? null])
  );

  const query = useQueries({
    queries: [
      {
        queryKey: ratesKeys.staticRate({ siteSlug, rateCode, language }),
        queryFn: () => getStaticRate({ siteSlug, rateCode, language }),
        cacheTime: Infinity,
        staleTime: Infinity,
        keepPreviousData: true,
      },
      {
        queryKey: ratesKeys.availableRate(rateCode, {
          siteId,
          ...{ ...params, isMultiRoomsEnabled },
        }),
        queryFn: () => getAvailableRate(rateCode, { siteId, ...params }, isMultiRoomsEnabled),
        keepPreviousData: true,
      },
    ],
  });

  const [staticRateQuery, availableRateQuery] = query;
  const staticRate = staticRateQuery.data?.data;
  const availableRate = availableRateQuery.data;

  let rate: Rate | undefined = undefined;

  if (staticRate) {
    rate = RateSchema.parse({
      id: staticRate.rateCode,
      title: staticRate.name ?? '',
      description: staticRate.description ?? '',
      imageUrl: staticRate.images?.[0] ?? '',
      images: staticRate.images ?? [],
      cancellationPolicyText: staticRate.cancellationPolicyText,
      price: availableRate?.fromPrice ? availableRate.fromPrice : undefined,
      rooms: availableRate
        ? availableRate.rooms
            .map((room) => ({ ...room, priority: roomPriorities.get(room.id) ?? null }))
            .sort(byRoomAvailabilityThenPriorityThenPrice)
        : [],
      uuid: staticRate._id,
      package: availableRate?.package ?? null,
    });
  }

  return {
    rate,
    isFetching: staticRateQuery.isFetching || availableRateQuery.isFetching,
    isLoading: staticRateQuery.isLoading || availableRateQuery.isLoading,
    query,
    error: staticRateQuery.error || availableRateQuery.error,
  };
}

export function useRatePageSettings(language: string, defaultTitle: string, enabled = true) {
  const { siteId } = useAppContext();

  const { data, isFetching } = useQuery({
    queryKey: ratesKeys.rateSettings({ siteId, language }),
    queryFn: () => getRatePageSettings({ siteId, language }),
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled,
  });

  return {
    content: {
      title: data?.title ?? defaultTitle,
      description: data?.content ?? null,
      heroBackgroundImage: data?.hero_background_image?.url ?? null,
      fallbackHeroBackgroundImage: data?.fallback_hero_background_image_url ?? null,
    },
    isFetching,
  };
}

export function useRoomRates(roomId: string, params: RateParams, language?: string) {
  const { siteId, siteSlug } = useAppContext();
  const isMultiRoomsEnabled =
    useFeatureIsOn('feature.multi-room.enabled') && params.rooms.length > 1;

  const query = useQueries({
    queries: [
      {
        queryKey: ratesKeys.staticRates({
          siteSlug,
          promoRateCode: params.promoRateCode,
          language,
        }),
        queryFn: () => getStaticRates({ siteSlug, promoRateCode: params.promoRateCode, language }),
        staleTime: 0,
        keepPreviousData: true,
      },
      {
        queryKey: roomsKeys.availableRoomById({
          id: roomId,
          siteId,
          isMultiRoomsEnabled,
          ...params,
        }),
        queryFn: () => getAvailableRoomById({ id: roomId, siteId, isMultiRoomsEnabled, ...params }),
        keepPreviousData: true,
      },
    ],
  });

  const [staticRatesQuery, roomRatesQuery] = query;
  const staticRates = staticRatesQuery.data?.data ?? [];
  const roomRates = roomRatesQuery.data?.rates ?? [];

  const rates = staticRates
    .reduce<Rate[]>((previous, staticRate) => {
      const roomRate = roomRates.find(({ id }) => id === staticRate.rateCode);

      if (roomRate) {
        return [
          ...previous,
          RateSchema.parse({
            id: roomRate.id,
            price: toCamelCase({ ...roomRate.from_price, prefix: '£', suffix: '' }),
            title: staticRate.name ?? '',
            description: staticRate.description ?? '',
            imageUrl: staticRate.images?.[0] ?? '',
            cancellationPolicyText: staticRate.cancellationPolicyText,
            priority: staticRate.priority ?? null,
            roomUpgrade: toCamelCase(roomRate.room_upgrade),
            quantity: roomRate.quantity,
            paymentTerms: toCamelCase(roomRate.payment_terms) ?? null,
          }),
        ];
      }

      return previous;
    }, [])
    .sort(byPriorityThenPrice);

  return {
    rates,
    isFetching: staticRatesQuery.isFetching || roomRatesQuery.isFetching,
    error: staticRatesQuery.error || roomRatesQuery.error,
    query,
  };
}
